import { AdvisorAssignmentSection } from './AdvisorAssignmentSection'
import { DummyLLMAssignmentSection } from './DummyLLMAssignmentSection'
import { PlayerAssignmentSection } from './PlayerAssignmentSection'
import { TeamNameCell } from './TeamNameCell'
import ConfirmationModal from '../OGE/OGEDeletionModal'

import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import React, { useCallback, useState } from 'react'
import CreateRoomModal from '../Room/CreateRoomModal'
import { ModeType, RoomMember, RoomModalMode } from '@/redux/types/room'
import { Team } from '@/redux/types/team'
import { Advisor } from '@/redux/types/advisor'
import { cn } from '@/lib/utils'

interface TeamsDesktopTableProps {
  teams: Team[]
  advisors: Advisor[]
  players: RoomMember[]
  isAdvisorAssignedToTeam: (advisorId: string, teamId: number) => boolean
  isPlayerAssignedToTeam: (playerId: string, teamId: number) => boolean
  onAdvisorToggle: (teamId: number, advisorId: string) => void
  onPlayerToggle: (teamId: number, playerId: string) => void
  dummyBots: Array<{ id: number; name: string }>
  isDummyAssignedToTeam: (dummyBotId: number, teamId: number) => boolean
  onDummyToggle: (teamId: number, dummyBotId: number) => void
  onUpdateTeam: (teamId: number, newName: string, description?: string) => Promise<void>
  onDeleteTeam: (teamId: number) => Promise<void>
  getTeamMemberCount: (teamId: number) => {
    players: number
    advisors: number
    total: number
  }
  hasTeamMembers: (teamId: number) => boolean
  tableColumnStyles: React.CSSProperties
  isReadOnly?: boolean
}

export function TeamsDesktopTable({
  teams,
  advisors,
  players,
  isAdvisorAssignedToTeam,
  isPlayerAssignedToTeam,
  onAdvisorToggle,
  onPlayerToggle,
  dummyBots,
  isDummyAssignedToTeam,
  onDummyToggle,
  onUpdateTeam,
  onDeleteTeam,
  getTeamMemberCount,
  hasTeamMembers,
  tableColumnStyles,
  isReadOnly = false,
}: TeamsDesktopTableProps) {
  const [editModalState, setEditModalState] = useState<{
    isOpen: boolean
    teamId: number | null
    teamName: string
    teamDescription: string
    isLoading: boolean
  }>({
    isOpen: false,
    teamId: null,
    teamName: '',
    teamDescription: '',
    isLoading: false,
  })

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean
    teamId: number | null
    teamName: string
  }>({
    isOpen: false,
    teamId: null,
    teamName: '',
  })

  const handleEditTeamClick = useCallback(
    (teamId: number) => {
      const team = teams.find((t) => t.id === teamId)
      if (team) {
        setEditModalState({
          isOpen: true,
          teamId,
          teamName: team.name,
          teamDescription: team.description || '',
          isLoading: false,
        })
      }
    },
    [teams]
  )

  const handleDeleteTeamClick = useCallback(
    (teamId: number) => {
      const team = teams.find((t) => t.id === teamId)
      if (team) {
        setDeleteModalState({
          isOpen: true,
          teamId,
          teamName: team.name,
        })
      }
    },
    [teams]
  )

  const handleEditModalClose = useCallback(() => {
    setEditModalState({
      isOpen: false,
      teamId: null,
      teamName: '',
      teamDescription: '',
      isLoading: false,
    })
  }, [])

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalState({ isOpen: false, teamId: null, teamName: '' })
  }, [])

  const handleEditConfirm = useCallback(
    async (newName: string, _timerMode?: unknown, description?: string) => {
      if (editModalState.teamId) {
        setEditModalState((prev) => ({ ...prev, isLoading: true }))
        try {
          await onUpdateTeam(editModalState.teamId, newName, description)
          handleEditModalClose()
        } catch (error) {
          console.error('Failed to update team:', error)
          setEditModalState((prev) => ({ ...prev, isLoading: false }))
        }
      }
    },
    [editModalState.teamId, onUpdateTeam, handleEditModalClose]
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteModalState.teamId) {
      await onDeleteTeam(deleteModalState.teamId)
      handleDeleteModalClose()
    }
  }, [deleteModalState.teamId, onDeleteTeam, handleDeleteModalClose])

  return (
    <div className='hidden lg:block'>
      <Card className='overflow-hidden'>
        <div className={cn(teams.length > 10 && 'overflow-x-auto')}>
          <div
            className={cn(
              'table-fixed',
              teams.length > 10 ? 'min-w-max' : 'w-full'
            )}
            style={tableColumnStyles}
          >
            <Table className='w-full'>
              <TableHeader className='bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10'>
                <TableRow>
                  <TableHead className='text-left p-4 font-semibold text-gray-700 border-r bg-gradient-to-r from-gray-50 to-gray-100 sticky left-0 z-20 w-[var(--name-column-width)]'>
                    Playing as
                  </TableHead>
                  {teams.map((team) => (
                    <TableHead
                      key={team.id}
                      className='text-center p-2 font-semibold text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 w-[var(--team-column-width)]'
                    >
                      <TeamNameCell
                        teamId={team.id}
                        teamName={team.name}
                        onEditTeam={handleEditTeamClick}
                        onDeleteTeam={handleDeleteTeamClick}
                        className='justify-center'
                        isReadOnly={isReadOnly}
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <AdvisorAssignmentSection
                  advisors={advisors}
                  teams={teams}
                  isAdvisorAssignedToTeam={isAdvisorAssignedToTeam}
                  onAdvisorToggle={onAdvisorToggle}
                  isReadOnly={isReadOnly}
                />
                <DummyLLMAssignmentSection
                  dummyBots={dummyBots}
                  teams={teams}
                  isDummyAssignedToTeam={isDummyAssignedToTeam}
                  onDummyToggle={onDummyToggle}
                  isReadOnly={isReadOnly}
                />
                <PlayerAssignmentSection
                  players={players}
                  teams={teams}
                  isPlayerAssignedToTeam={isPlayerAssignedToTeam}
                  onPlayerToggle={onPlayerToggle}
                  isReadOnly={isReadOnly}
                />
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      {!isReadOnly && (
        <>
          <CreateRoomModal
            modeType={ModeType.TEAM}
            isOpen={editModalState.isOpen}
            onClose={handleEditModalClose}
            onConfirm={handleEditConfirm}
            isLoading={editModalState.isLoading}
            mode={RoomModalMode.EDIT}
            currentRoomName={editModalState.teamName}
            currentDescription={editModalState.teamDescription}
          />

          <ConfirmationModal
            isOpen={deleteModalState.isOpen}
            onClose={handleDeleteModalClose}
            onConfirm={handleDeleteConfirm}
            title='Delete Team'
            description={
              deleteModalState.teamId && hasTeamMembers(deleteModalState.teamId)
                ? `Are you sure you want to delete "${deleteModalState.teamName}"? This team has ${getTeamMemberCount(deleteModalState.teamId).total} member(s) assigned. Deleting will unassign them.`
                : `Are you sure you want to delete "${deleteModalState.teamName}"? This action cannot be undone.`
            }
            confirmButtonText='Delete'
            cancelButtonText='Cancel'
          />
        </>
      )}
    </div>
  )
}
